import { useState } from 'react';
import { RESCUE_STATUS_LABEL, type Grade, type LeadJourneyResponse, type RescueStatus, type Tone } from '@coachrex/shared';
import { useAddToRescue, useChangeGrade, useUndoGrade, useUndoRescue } from '../lib/api';
import { relativeStamp } from '../lib/format';
import { Modal } from '../components/Modal';
import { StatusModal } from '../components/StatusModal';
import { useToast } from '../components/Toast';
import { Chip, ErrorBox, GradeBadge } from '../components/ui';

const STATUS_TONE: Record<RescueStatus, Tone> = { open: 'red', completed: 'blue', recovered: 'green', lost: 'grey', not_needed: 'grey' };
const GRADES: Grade[] = ['A', 'B', 'C', 'D'];

/** Lead journey → "Add to rescue queue" (header). Hidden while the lead has an active item. */
export function AddToRescueButton({ data }: { data: LeadJourneyResponse }) {
  const [open, setOpen] = useState(false);
  const active = data.rescue && (data.rescue.status === 'open' || data.rescue.status === 'completed');
  if (active) return null;
  return (
    <>
      <button className="btn btn--secondary" onClick={() => setOpen(true)}>
        Add to rescue queue
      </button>
      {open && <AddToRescueModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

function AddToRescueModal({ data, onClose }: { data: LeadJourneyResponse; onClose: () => void }) {
  const add = useAddToRescue(data.lead.id);
  const undo = useUndoRescue();
  const toast = useToast();
  const [note, setNote] = useState('');
  const first = data.lead.name.split(' ')[0];
  const save = () => {
    if (!note.trim() || add.isPending) return;
    add.mutate(note.trim(), {
      onSuccess: ({ undoId }) => {
        onClose();
        toast({ text: `${first} added to the rescue queue`, undo: () => undo.mutateAsync(undoId) });
      },
    });
  };
  return (
    <Modal title="Add to rescue queue" subtitle="The reason shows in the queue and is added as a note on the contact in your CRM." onClose={onClose} width={480}>
      <label className="form-label">
        <span>Why is {first} at risk?</span>
        <textarea className="text-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Asked for a callback after 5pm, nobody called." autoFocus />
      </label>
      {add.error && <ErrorBox error={add.error} />}
      <div className="modal__actions">
        <button className="btn btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn--accent" disabled={!note.trim() || add.isPending} onClick={save}>
          {add.isPending ? 'Adding…' : 'Add to queue'}
        </button>
      </div>
    </Modal>
  );
}

/** Lead journey → Rescue card: status, "Update status", history ending with "Flagged for rescue · Coach Lex". */
export function RescueCard({ data }: { data: LeadJourneyResponse }) {
  const r = data.rescue;
  const [editing, setEditing] = useState(false);
  if (!r) return null;
  return (
    <section className="card stack-12">
      <div className="card-head" style={{ alignItems: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="card-title">Rescue</span>
          <Chip tone={STATUS_TONE[r.status]}>{RESCUE_STATUS_LABEL[r.status]}</Chip>
        </span>
        <button className="btn btn--secondary btn--sm" onClick={() => setEditing(true)}>
          Update status
        </button>
      </div>
      <span style={{ fontSize: 13, color: 'var(--body)' }}>{r.reason}</span>
      <div className="rescue-history">
        {r.history.map((h) => (
          <div key={h.id} className="rescue-history__item">
            <span className="rescue-history__dot" />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13 }}>
                <b>{RESCUE_STATUS_LABEL[h.status]}</b>
                {h.note && h.note !== 'Flagged for rescue' ? ` · ${h.note}` : h.note ? ' · Flagged for rescue' : ''}
              </span>
              <span className="card-meta">
                {h.manual ? `Set by ${h.by}` : h.by}
                {h.credit ? ` · credit ${h.credit}` : ''} · {relativeStamp(h.at, data.timeZone)}
              </span>
            </span>
          </div>
        ))}
      </div>
      {editing && <StatusModal item={{ id: r.id, leadName: data.lead.name, status: r.status }} onClose={() => setEditing(false)} />}
    </section>
  );
}

/** "Change grade" on the Lead grade card. Note required; written to the CRM; Undo for 6 seconds. */
export function ChangeGradeButton({ data }: { data: LeadJourneyResponse }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
        Change grade
      </button>
      {open && <ChangeGradeModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

function ChangeGradeModal({ data, onClose }: { data: LeadJourneyResponse; onClose: () => void }) {
  const change = useChangeGrade(data.lead.id);
  const undo = useUndoGrade(data.lead.id);
  const toast = useToast();
  const current = data.grade.final;
  const [grade, setGrade] = useState<Grade | null>(null);
  const [note, setNote] = useState('');
  const canSave = !!grade && grade !== current && note.trim().length > 0 && !change.isPending;
  const save = () => {
    if (!canSave || !grade) return;
    change.mutate(
      { grade, note: note.trim() },
      {
        onSuccess: ({ undoId }) => {
          onClose();
          toast({ text: `Grade changed to ${grade}`, undo: () => undo.mutateAsync(undoId) });
        },
      },
    );
  };
  return (
    <Modal
      title="Change grade"
      subtitle={`${data.grade.ai ? `The AI graded ${data.lead.name.split(' ')[0]} ${data.grade.ai}. ` : ''}Your grade wins over the AI’s and is labelled with your name. The note is added to the contact in your CRM.`}
      onClose={onClose}
      width={480}
    >
      <div className="form-label">
        <span>New grade</span>
        <div style={{ display: 'flex', gap: 10 }}>
          {GRADES.map((g) => (
            <button key={g} className={`grade-pick${grade === g ? ' is-active' : ''}`} disabled={g === current} onClick={() => setGrade(g)} aria-pressed={grade === g} aria-label={`Grade ${g}`}>
              <GradeBadge grade={g} size={26} />
            </button>
          ))}
        </div>
      </div>
      <label className="form-label">
        <span>Why?</span>
        <textarea className="text-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Confirmed financing on a second call." />
      </label>
      {change.error && <ErrorBox error={change.error} />}
      <div className="modal__actions">
        <button className="btn btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn--accent" disabled={!canSave} onClick={save}>
          {change.isPending ? 'Saving…' : 'Save grade'}
        </button>
      </div>
    </Modal>
  );
}
